import axios from 'axios';

let cancelTokenSources = [];

const AUTH_TOKEN_KEY = 'mantraz_auth_token';
const REFRESH_TOKEN_KEY = 'mantraz_refresh_token';
const ACTIVE_COMPANY_KEY = 'activeCompany';
const REFRESH_PATH = '/security/authentication/refresh';
const SET_COMPANY_PATH = '/security/authentication/setcompany';
const LOGIN_PATH = '/security/authentication/login';
const STATUS_PATH = '/security/authentication/status';
const AUTH_ERROR_CODE = 411;
// Flag-flag UI session yang harus ikut dibersihkan saat logout / auth gagal.
const SESSION_STATE_KEYS = ['isAuthenticated', 'userId', 'activeCompany', 'activeGroup', 'activeApp'];

const baseURL = process.env.VUE_APP_API_URL ? process.env.VUE_APP_API_URL : '/';

/**
 * Amplop respons `/master/*` dan sebagian besar endpoint lain di API ini.
 *
 * Dua konvensi unwrap hidup bareng: service yang balikin body
 * (`const { data } = await ...`) dibaca `body.data`, service yang balikin response
 * axios utuh dibaca `res.data.data`. Tiap @returns di `src/services/` nyebut yang mana.
 *
 * Tiga kejanggalan auth yang dijaga di file ini beda-beda, jangan dilebur:
 * - auth error datang sebagai HTTP 404 + `status.errorCode` 411 -> isAuthError
 * - errorCode 411 juga datang dengan HTTP 200, axios gak throw -> isAuthErrorBody
 * - `/security/authentication/status` balas HTTP 200 tanpa `user` pas access token
 *   expired, bukan 411 -> isAnonymousStatusResponse
 */

/**
 * @typedef {Object} ApiStatus
 * @property {number} error 0 = sukses, selain nol = gagal
 * @property {number} errorCode arti beda per endpoint: 411 auth, 412 ditolak izin,
 *   400 dan 401 validasi. Jangan diseragamkan jadi "error".
 * @property {string} message teks siap tampil dari backend
 */

/**
 * @typedef {Object} ApiPagination
 * @property {number} page halaman yang dibalikin, 1-based
 * @property {number} itemPerPage jumlah baris per halaman
 * @property {number} totalData total baris di seluruh halaman
 */

/**
 * Body list `/master/*`.
 *
 * Kosakata pagination beda antar lapisan: backend kirim
 * `pagination.{page,itemPerPage,totalData}`, komponen list lama pakai
 * `{current,pageSize,totalSize}` dan nerjemahin sendiri. Param KIRIM mayoritas
 * `page` + `max`, bukan `itemPerPage`.
 *
 * @typedef {Object} MasterListBody
 * @property {Object.<string, {Field: string}>} fields deskriptor kolom, OBJEK bukan
 *   array — pemanggil pakai `Object.values(...)` lalu baca `.Field`
 * @property {Object[]} data baris hasil
 * @property {ApiPagination} pagination
 * @property {ApiStatus} status
 */

/**
 * Body create / update / delete `/master/*`.
 *
 * RAGU: `affected_rows` belum dicek ke payload nyata — nol pemanggil membacanya.
 *
 * @typedef {Object} MasterMutationBody
 * @property {number} [id] id record baru, cuma ada di create
 * @property {number} [affected_rows] jumlah baris kena update/delete
 * @property {ApiStatus} status
 */

const axiosInstance = axios.create({
  baseURL,
});

export function getNewCancelToken() {
  const source = axios.CancelToken.source();
  cancelTokenSources.push(source);
  return source.token;
}

export function cancelPendingRequests() {
  cancelTokenSources.forEach(source => source.cancel('Request Cencelled.'));
  cancelTokenSources = [];
}

// --- Token storage ---------------------------------------------------------

// Simpan access token & refresh token. Dipakai saat login dan saat rotasi token.
export function setAuthTokens(token, refreshToken) {
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  }
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

// Bersihkan seluruh state auth: tokens + flag session.
// sessionStorage juga ikut dibersihkan utk membuang sisa state dari versi lama.
export function clearAuthSession() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  SESSION_STATE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
}

// --- Refresh token handling ------------------------------------------------

// True ketika API membalas auth error (HTTP 404 dengan errorCode 411).
function isAuthError(error) {
  return !!(error && error.response
    && error.response.data
    && error.response.data.status
    && error.response.data.status.errorCode === AUTH_ERROR_CODE);
}

// Bersihkan sesi lalu arahkan ke halaman login.
function handleAuthFailure() {
  clearAuthSession();
  if (!window.location.pathname.includes('/auth/login')) {
    window.location.href = '/auth/login';
  }
}

// /status itu spesial: sesi invalid/access-token expired dibalikinnya HTTP 200
// + body kosong (gak ada `user`), bukan errorCode 411 kayak endpoint lain -
// jadi gak pernah nyentuh interceptor error di bawah. Tanpa ini, page reload
// pas access token expired langsung hard-logout walau refresh token-nya masih
// hidup.
// Cuma dianggap "access token expired" kalau tadinya emang ada token - visitor
// yang dari awal belum login (mis. buka halaman forgot-password) harus dibiarkan
// anonim, bukan dilempar ke alur refresh (yang bakal gagal & redirect ke login).
function isAnonymousStatusResponse(response) {
  return !!(response && response.config && response.config.url
    && response.config.url.includes(STATUS_PATH)
    && response.data && !response.data.user
    && localStorage.getItem(AUTH_TOKEN_KEY));
}

// Backend balikin 411 dengan HTTP 200 (bukan cuma di /status) - axios nganggep
// sukses, jadi dicek manual di sini juga.
function isAuthErrorBody(data) {
  return !!(data && data.status && data.status.errorCode === AUTH_ERROR_CODE);
}

let isRefreshing = false;
let refreshPromise = null;

function getActiveCompanyId() {
  const rawCompany = localStorage.getItem(ACTIVE_COMPANY_KEY);
  if (!rawCompany) {
    return null;
  }

  try {
    const parsedCompany = JSON.parse(rawCompany);
    if (!parsedCompany || typeof parsedCompany !== 'object') {
      return null;
    }

    return parsedCompany.compId || parsedCompany.companyId || parsedCompany.id || null;
  } catch (e) {
    return null;
  }
}

function reapplyActiveCompany(token) {
  const companyId = getActiveCompanyId();
  if (!companyId || !token) {
    return Promise.resolve();
  }

  return axios.get(`${baseURL}${SET_COMPANY_PATH}/${companyId}`, {
    headers: {
      Authentication: token,
    },
  });
}

// Tukar refreshToken dengan token baru. Memakai axios polos (tanpa interceptor)
// supaya kegagalan refresh tidak memicu refresh berulang.
function refreshAuthToken() {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token available'));
  }

  const formData = new FormData();
  formData.append('refreshToken', refreshToken);

  return axios
    .post(`${baseURL}${REFRESH_PATH}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 15000,
    })
    .then(async (res) => {
      const data = res.data || {};
      if (!data.token) {
        throw new Error('Refresh response missing token');
      }
      // Token & refreshToken keduanya berotasi pada setiap refresh.
      setAuthTokens(data.token, data.refreshToken);
      // Reapply company itu side-effect sekunder: kalau gagal (mis. company
      // keburu non-aktif), jangan gagalin refresh yang udah sukses.
      await reapplyActiveCompany(data.token).catch((err) => {
        console.warn('Gagal reapply active company setelah refresh token:', err);
      });
      return data.token;
    });
}

// --- Interceptors ----------------------------------------------------------

// Refresh token sekali (di-dedupe lewat isRefreshing/refreshPromise walau
// banyak request nabrak bersamaan) lalu ulangi request aslinya. Dipakai baik
// dari response sukses (kasus /status kosong) maupun response error (411).
function refreshAndRetry(instance, originalRequest, fallbackError) {
  // Sudah pernah di-retry -> refresh tidak menolong, hentikan sesi.
  if (originalRequest._retry) {
    handleAuthFailure();
    return Promise.reject(fallbackError);
  }
  originalRequest._retry = true;

  if (!isRefreshing) {
    isRefreshing = true;
    refreshPromise = refreshAuthToken().then(
      (token) => {
        isRefreshing = false;
        return token;
      },
      (refreshError) => {
        isRefreshing = false;
        handleAuthFailure();
        return Promise.reject(refreshError);
      },
    );
  }

  return refreshPromise
    .then(() => instance(originalRequest))
    .catch(() => Promise.reject(fallbackError));
}

function applyAuthInterceptors(instance) {
  // Selalu sisipkan token terbaru dari localStorage pada setiap request.
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authentication = token;
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      // Login gak boleh masuk alur refresh-lalu-retry.
      if (response.config?.url?.includes(LOGIN_PATH)) {
        return response;
      }

      if (isAuthErrorBody(response.data) || isAnonymousStatusResponse(response)) {
        return refreshAndRetry(instance, response.config, response);
      }
      return response;
    },
    (error) => {
      const originalRequest = error.config;

      // Login gak boleh masuk alur refresh-lalu-retry: gagal login (credentials
      // salah) bukan "token expired", dan belum tentu ada refresh token buat dipakai.
      if (!originalRequest || !isAuthError(error) || originalRequest.url?.includes(LOGIN_PATH)) {
        return Promise.reject(error);
      }

      return refreshAndRetry(instance, originalRequest, error);
    },
  );

  return instance;
}

applyAuthInterceptors(axiosInstance);

// Compatibility shim for legacy services that call axiosInstance.create(...).
axiosInstance.create = function create(config = {}) {
  return applyAuthInterceptors(axios.create({
    ...axiosInstance.defaults,
    ...config,
  }));
};

export default axiosInstance;
